import React from 'react';
import { twMerge } from 'tailwind-merge';

/**
 * Accordion — canonical disclosure section (Spec 240).
 *
 * Encapsulates the long-standing hand-rolled `.collapsible-header` pattern
 * (`<button class="collapsible-header"><h2>title</h2><span class="collapse-icon">±</span></button>`
 * + `.collapsible-content`) so ~20 pages stop hand-rolling the raw <button>.
 * Renders the SAME markup + reuses the SAME CSS classes (web/src/styles/components.css
 * §.collapsible-*), so adopting it is pixel-identical — the only change is the raw
 * <button> moving into ui/ (where the design-audit ratchet doesn't count it).
 *
 * CONTROLLED: collapse state stays with the caller (most pages drive several
 * sections from one `collapsedSections` object), so pass `collapsed` + `onToggle`.
 */
export interface AccordionProps {
  title: React.ReactNode;
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  /** Extra wrapper classes (e.g. 'settings-section' / 'danger-section') some pages add. */
  className?: string;
  /** Optional id for the content region (a11y / scroll targets). */
  id?: string;
}

export const Accordion: React.FC<AccordionProps> = ({
  title,
  collapsed,
  onToggle,
  children,
  className = '',
  id,
}) => (
  <div className={twMerge('collapsible-section', collapsed ? 'collapsed' : '', className)}>
    <button
      type="button"
      className="collapsible-header"
      onClick={onToggle}
      aria-expanded={!collapsed}
    >
      <h2>{title}</h2>
      <span className="collapse-icon" aria-hidden="true">{collapsed ? '+' : '−'}</span>
    </button>
    <div className="collapsible-content" id={id}>
      {children}
    </div>
  </div>
);

Accordion.displayName = 'Accordion';
